import React, { forwardRef, ForwardRefRenderFunction } from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { AutoCompleteExtraProps, FormHandles, TableListFieldProps } from "models/propTypes/genericFormProps";
import { CurrencyEnums, FormEnums } from "models/enums";
import { EmployeesSettingsFormProps } from "models/propTypes/settingsPropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { SettingsFormTypes } from "models/formTypes";
import { TableRowProperties } from "models/propTypes/simpleTableProps";
//#endregion
//#region components
import { GenericForm, Tags } from "components/common";
import { useEmployeeHelpers } from "components/employee";
import { isEmployeeSalaryActive } from "components/employee/employeeHelpers";
import { Chip, Grid } from "@mui/material";
import { useSalaryService } from "serviceHooks";
import useSalaryPermissions from "components/salary/hooks/useSalaryPermissions";
//#endregion
import { TranslationKeys } from "translations";
import cssExports from "theme/styles/main.module.scss";
import { LocalizationHelperInstance } from "utils/helpers";

const EmployeesSettingsForm: ForwardRefRenderFunction<FormHandles, EmployeesSettingsFormProps> = (props, ref) => {
  const salaryService = useSalaryService();
  const { employeesMoreActions, addOrEditEmployee, getEmployeePermissions } = props;
  const { canCreate, canManageEmployeeSalary, canManageSalarySettings, canSalaryAdminManageSalary } =
    useEmployeeHelpers();
  const { isWintSalarySettingsAvailable } = useSalaryPermissions();
  const selectedCompanyId = useSelector((state: AppState) => state.auth.selectedCompanyId);

  const getEmployeesTableProperties = () => {
    const userTypeNamesById = new Map(props.enabledUserTypes.map((type) => [type.id, type.name]));

    const properties: Partial<Record<keyof any, TableRowProperties>> = {
      name: {
        formatter: (value: WintSuperkollAPIModel.EmployeeItem) => {
          const userTypeName =
            props.userTypesEnabled && value.userTypeId != null ? userTypeNamesById.get(value.userTypeId) : undefined;

          return (
            <>
              <Grid item className={cssExports.tableListMainText}>
                {value.name}
              </Grid>
              {props.isAdmin && value.personnumber}
              {userTypeName && <Tags tags={[userTypeName]} />}
            </>
          );
        },
      },
    };

    canManageSalarySettings &&
      (properties[`salaryInfo`] = {
        formatter: (value: WintSuperkollAPIModel.EmployeeItem) =>
          canManageEmployeeSalary(value.id) ? (
            <>
              {value.salaryInfo?.wintSalarySpecificSettings?.hourlySalary != null &&
              value.salaryInfo.wintSalarySpecificSettings.hourlySalary !== 0 ? (
                <Grid item className={cssExports.tableListMainText}>
                  {`${LocalizationHelperInstance.getFormattedCurrency(
                    value.salaryInfo.wintSalarySpecificSettings.hourlySalary,
                    CurrencyEnums.CurrencyCode.SEK
                  )}/tim`}
                </Grid>
              ) : (
                value.salaryInfo?.monthlySalary != null &&
                value.salaryInfo.monthlySalary !== 0 && (
                  <Grid item className={cssExports.tableListMainText}>
                    {`${LocalizationHelperInstance.getFormattedCurrency(
                      value.salaryInfo.monthlySalary,
                      CurrencyEnums.CurrencyCode.SEK
                    )}/mån`}
                  </Grid>
                )
              )}
              <Grid item>
                {value.salaryInfo?.bankAccountClearing && value.salaryInfo?.bankAccountClearing}
                {value.salaryInfo?.bankAccount && ` - ${value.salaryInfo?.bankAccount}`}
              </Grid>
              {((value.salaryInfo?.monthlySalary != null && value.salaryInfo.monthlySalary !== 0) ||
                (value.salaryInfo?.wintSalarySpecificSettings?.hourlySalary != null &&
                  value.salaryInfo.wintSalarySpecificSettings.hourlySalary !== 0)) &&
                value.salaryInfo?.employmentRate != null &&
                value.salaryInfo.employmentRate !== 0 && <Grid item>{`${value.salaryInfo.employmentRate} %`}</Grid>}
              {((value.salaryInfo?.monthlySalary != null && value.salaryInfo.monthlySalary !== 0) ||
                (value.salaryInfo?.wintSalarySpecificSettings?.hourlySalary != null &&
                  value.salaryInfo.wintSalarySpecificSettings.hourlySalary !== 0)) &&
                (value.salaryInfo?.wintSalarySpecificSettings?.taxPercentage != null ? (
                  <Grid
                    item
                  >{`${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.TaxAdjustment)} ${Math.round(value.salaryInfo.wintSalarySpecificSettings.taxPercentage * 100)} %`}</Grid>
                ) : (
                  value.salaryInfo?.taxTable != null &&
                  String(value.salaryInfo.taxTable) !== "0" && (
                    <Grid item>{`${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.TaxTable)} ${
                      value.salaryInfo.taxTable
                    }`}</Grid>
                  )
                ))}
            </>
          ) : (
            <></>
          ),
        maxWidth: "300px",
      });

    properties[`mailInfo`] = {
      formatter: (value: WintSuperkollAPIModel.EmployeeItem) => (
        <>
          <Grid item className={cssExports.tableListMainText}>
            {value.mail}
          </Grid>
          {props.isAdmin && (
            <Grid item>
              {value.salaryInfo?.employmentStartDate &&
                LocalizationHelperInstance.getFormattedDate(value.salaryInfo?.employmentStartDate)}
              {value.salaryInfo?.employmentEndDate &&
                ` till ${LocalizationHelperInstance.getFormattedDate(value.salaryInfo.employmentEndDate)}`}
            </Grid>
          )}
          {props.isAdmin && getEmployeePermissions(value)}
        </>
      ),
      maxWidth: "500px",
    };
    canManageSalarySettings &&
      (properties[`isActive`] = {
        formatter: (value: WintSuperkollAPIModel.EmployeeItem) => {
          if (!canManageEmployeeSalary(value.id)) return <></>;

          const isSalaryActive = isEmployeeSalaryActive(value, isWintSalarySettingsAvailable);

          return (
            <Chip
              label={LocalizationHelperInstance.getFormattedMessage(
                isSalaryActive ? TranslationKeys.Settings.SalaryActive : TranslationKeys.Settings.SalaryInactive
              )}
              className={isSalaryActive ? cssExports.greenTag : cssExports.redTag}
            />
          );
        },
        alignment: "center",
        width: "100px",
      });

    return properties;
  };

  return (
    <GenericForm<SettingsFormTypes.EmployeesSettings>
      formRef={ref}
      inline
      {...props}
      fields={[
        {
          label: TranslationKeys.Common.Generally,
          type: FormEnums.FormSectionType.FIELDGROUPSECTION,
          icon: IconDefinitionConstants.WInfo,
          hidden: !canSalaryAdminManageSalary,
          innerFields: {
            defaultSalaryDeviationPersonName: {
              label: TranslationKeys.Settings.DefaultSalaryDeviationPerson,
              descriptionText: TranslationKeys.Settings.DefaultSalaryDeviationPersonDescription,
              type: FormEnums.FormFieldType.AUTOCOMPLETE,
              hidden: !canSalaryAdminManageSalary,
              extraProps: {
                items: salaryService.searchSalaryAdmins,
                referenceFieldName: FormFieldConstants.Settings.EmployeesSettings.DefaultSalaryDeviationPersonId,
              } as AutoCompleteExtraProps<SettingsFormTypes.EmployeesSettings>,
            },
            deviationsViaMobile: {
              label: TranslationKeys.Settings.DeviationsViaMobile,
              descriptionText: TranslationKeys.Settings.DeviationsViaMobileDescription,
              type: FormEnums.FormFieldType.TOGGLE,
              hidden: !(
                props.isSalaryAdmin &&
                props.superkollSalaryType === WintSuperkollAPIModel.SuperkollSalaryType.Deviations
              ),
            },
          },
        },
        {
          label: TranslationKeys.Common.Users,
          type: FormEnums.FormSectionType.FIELDGROUPSECTION,
          icon: IconDefinitionConstants.WUser,
          inline: false,
          innerFields: {
            employees: {
              label: "",
              type: FormEnums.FormFieldType.TABLELIST,
              extraProps: {
                resultTableProperties: getEmployeesTableProperties(),
                addItemFunction: (values: WintSuperkollAPIModel.EmployeeItem) => {
                  addOrEditEmployee(values);
                },
                hideAddNewButton: !canCreate,
                moreActions: employeesMoreActions,
                orderByProperty: FormFieldConstants.Settings.EmployeeSettings.Name,
                entityText: TranslationKeys.Common.User,
                stayOnCurrentPage: true,
                // Editing a user opens a separate page, so closing it would otherwise start the list over from page one.
                rememberPositionKey: `settingsEmployeesListPosition_${selectedCompanyId}`,
                restorePosition: props.restoreListPosition,
                // The personal identity number is only rendered for admins, so only admins
                // may search on it - otherwise a non-admin could confirm who a number belongs to.
                searchKeys: props.isAdmin ? ["name", "mail", "personnumber"] : ["name", "mail"],
                searchPlaceholder: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.SearchUsers),
              } as TableListFieldProps<WintSuperkollAPIModel.EmployeeItem>,
            },
          },
        },
      ]}
    />
  );
};

export default forwardRef(EmployeesSettingsForm);
